import { BookmarkSimple, Copy, FilmStrip, MagicWand } from '@phosphor-icons/react';
import type { SceneReplicaDto } from '@reelplicate/api-client/model';
import { Button, Inline, Stack, Tag, toast } from '@reelplicate/ui';
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import { timecode } from './scenes';
import type { Asset, Collection, Scene } from './types';

/**
 * A reel's or clip's video, playing for real: a saved reel's kept video, else its file. `scene` plays just that stretch:
 * it seeks to the start, plays, and pauses at the end.
 */
export type VideoShape = 'portrait' | 'landscape' | 'square';

export function ScenePlayer({
  asset,
  scene,
  shape: knownShape,
  onShape,
}: {
  asset: Asset;
  /** The scene to play, with a key that changes on every request (play it again). */
  scene?: { start: number; end: number; key: number };
  /** Its shape, when the caller already knows it (kept across a re-layout). */
  shape?: VideoShape;
  /** Told the video's shape once it's known. */
  onShape?: (shape: VideoShape) => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const stopAt = useRef<number | null>(null);
  // The box takes the video's own shape once it's known: portrait until then.
  const [ownShape, setShape] = useState<VideoShape>('portrait');
  const shape = knownShape ?? ownShape;

  useEffect(() => {
    const el = video.current;
    if (!el || !scene) return;
    stopAt.current = scene.end;
    el.currentTime = scene.start;
    // A browser that refuses autoplay leaves it paused at the scene's start
    // (and older ones return nothing from play()).
    Promise.resolve(el.play()).catch(() => undefined);
  }, [scene]);

  return (
    <video
      ref={video}
      src={asset.videoUrl ?? asset.fileUrl}
      poster={asset.thumbnail}
      controls
      playsInline
      preload="metadata"
      aria-label={`Video of ${asset.title}`}
      data-shape={shape}
      className={`bg-bg-inverse shrink-0 rounded-md object-contain ${
        shape === 'landscape'
          ? 'aspect-video h-auto w-full'
          : shape === 'square'
            ? 'size-60'
            : 'h-80 w-45'
      }`}
      onLoadedMetadata={(e) => {
        const { videoWidth: w, videoHeight: h } = e.currentTarget;
        if (!w || !h) return;
        const next = w > h * 1.05 ? 'landscape' : h > w * 1.05 ? 'portrait' : 'square';
        setShape(next);
        onShape?.(next);
      }}
      onTimeUpdate={(e) => {
        if (stopAt.current !== null && e.currentTarget.currentTime >= stopAt.current) {
          e.currentTarget.pause();
          stopAt.current = null;
        }
      }}
      // Seeking by hand ends "play this scene".
      onSeeking={(e) => {
        if (stopAt.current !== null && e.currentTarget.currentTime > stopAt.current) {
          stopAt.current = null;
        }
      }}
    />
  );
}

/**
 * A reel's scenes, in order: a frame, its time, what's on screen, what's
 * said and what it shows. Choosing a scene plays it; each can be saved as a
 * clip, its on-screen text copied or saved as a caption, or remade as an AI
 * video. Saved scenes say where to.
 */
export function SceneList({
  scenes,
  current,
  onPlay,
  saved,
  onSaveCaption,
  onSaveClip,
  savingScene,
  onReplicate,
  replicas,
}: {
  scenes: Scene[];
  current?: number;
  onPlay?: (scene: Scene) => void;
  /** Per scene: collections its captions are in, and its clips. */
  saved: Map<number, { captionsIn?: Collection[]; clips: Asset[] }>;
  onSaveCaption?: (scene: Scene) => void;
  onSaveClip?: (scene: Scene) => void;
  savingScene?: number;
  /** Remake the scene as an AI video. */
  onReplicate?: (scene: Scene) => void;
  /** Per scene: its latest replica. */
  replicas?: Map<number, SceneReplicaDto>;
}) {
  const idPrefix = useId();
  const copyCaption = async (scene: number, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Caption copied');
    } catch {
      // No clipboard (plain http, old browsers): select it to copy by hand.
      const node = document.getElementById(`${idPrefix}-text-${scene}`);
      if (node) window.getSelection()?.selectAllChildren(node);
      toast.info('Caption selected - copy it from the menu');
    }
  };
  return (
    <Stack as="ol" gap={1} className="bg-bg-subtle rounded-[10px] p-1.5">
      {scenes.map((scene) => {
        const savedHere = saved.get(scene.scene);
        const replica = replicas?.get(scene.scene);
        const body = (
          <Inline gap={3} alignY="top" className="w-full">
            {scene.frameUrl ? (
              <img
                src={scene.frameUrl}
                alt=""
                className="aspect-[9/16] w-9 shrink-0 rounded-sm object-cover"
              />
            ) : (
              <span className="bg-bg-muted aspect-[9/16] w-9 shrink-0 rounded-sm" />
            )}
            <Stack gap={0.5} className="min-w-0 flex-1">
              <span className="text-caption text-text-tertiary tabular-nums">
                Scene {scene.scene} · {timecode(scene.start)}–{timecode(scene.end)}
              </span>
              {scene.text && (
                <span
                  id={`${idPrefix}-text-${scene.scene}`}
                  className="text-body-sm text-text-primary whitespace-pre-line"
                >
                  {scene.text}
                </span>
              )}
              {scene.speech && (
                <span className="text-body-sm text-text-secondary">“{scene.speech}”</span>
              )}
              {scene.context && (
                <span className="text-caption text-text-tertiary italic">
                  {scene.context}
                </span>
              )}
            </Stack>
          </Inline>
        );
        const row =
          'w-full rounded-md px-2 py-1.5 text-left outline-none hover:bg-bg-muted focus-visible:shadow-focus-ring aria-current:bg-bg-muted';
        return (
          <Stack as="li" key={scene.scene} gap={1}>
            {onPlay ? (
              <button
                type="button"
                aria-current={scene.scene === current || undefined}
                aria-label={`Play scene ${scene.scene}`}
                onClick={() => onPlay(scene)}
                className={row}
              >
                {body}
              </button>
            ) : (
              <div className={row}>{body}</div>
            )}
            <Inline gap={1.5} wrap alignY="center" className="pb-1.5 pl-14">
              {onSaveClip && (
                <Button
                  size="sm"
                  variant="outline"
                  leadingIcon={FilmStrip}
                  disabled={savingScene !== undefined}
                  onClick={() => onSaveClip(scene)}
                >
                  {savingScene === scene.scene ? 'Saving clip…' : 'Save clip'}
                </Button>
              )}
              {scene.text && (
                <Button
                  size="sm"
                  variant="outline"
                  leadingIcon={Copy}
                  aria-label={`Copy scene ${scene.scene} caption`}
                  onClick={() => void copyCaption(scene.scene, scene.text ?? '')}
                >
                  Copy caption
                </Button>
              )}
              {onSaveCaption && scene.text && (
                <Button
                  size="sm"
                  variant="outline"
                  leadingIcon={BookmarkSimple}
                  onClick={() => onSaveCaption(scene)}
                >
                  Save caption
                </Button>
              )}
              {onReplicate && scene.frameUrl && (
                <Button
                  size="sm"
                  variant="outline"
                  leadingIcon={MagicWand}
                  onClick={() => onReplicate(scene)}
                >
                  Replicate
                </Button>
              )}
              {replica?.status === 'DONE' && replica.resultAssetId ? (
                <Link
                  to={`/media?asset=${replica.resultAssetId}`}
                  className="focus-visible:shadow-focus-ring rounded-sm outline-none"
                >
                  <Tag>Replica ready</Tag>
                </Link>
              ) : replica?.status === 'FAILED' ? (
                <span title={replica.error ?? undefined}>
                  <Tag>Replica failed</Tag>
                </span>
              ) : replica ? (
                <Tag>Replicating…</Tag>
              ) : null}
              {savedHere?.clips.map((clip) => (
                <Link
                  key={clip.id}
                  to={`/media?asset=${clip.id}`}
                  className="focus-visible:shadow-focus-ring rounded-sm outline-none"
                >
                  <Tag>Clip saved</Tag>
                </Link>
              ))}
              {savedHere?.captionsIn &&
                (savedHere.captionsIn.length > 0 ? (
                  savedHere.captionsIn.map((c) => (
                    <Link
                      key={c.id}
                      to={`/media/collections/${c.id}`}
                      className="focus-visible:shadow-focus-ring rounded-sm outline-none"
                    >
                      <Tag>Caption · {c.name}</Tag>
                    </Link>
                  ))
                ) : (
                  <Link
                    to="/media?type=caption"
                    className="focus-visible:shadow-focus-ring rounded-sm outline-none"
                  >
                    <Tag>Caption saved</Tag>
                  </Link>
                ))}
            </Inline>
          </Stack>
        );
      })}
    </Stack>
  );
}
